/* ==========================================================================
   Executive Beverage — Private cocktail class booking

   Three jobs:
     1. Cap the cocktail picker at N and say so, rather than letting someone
        tick nine and find out later that only three were possible.
     2. Keep a live total, because "$75 per person" means nothing until you
        multiply it by your actual headcount.
     3. Let an occasion fill the picker in one click. Choosing three cocktails
        from a list you have never tasted is the hardest ask on the page, so
        every occasion carries a trio that ticks itself.

   Progressive enhancement throughout: without this file the form is still a
   working contact form. The cap is the only thing lost, and the server-side
   submission carries whatever was ticked either way.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-eb-class]');
  if (!root) return;

  var PER_HEAD = parseFloat(root.getAttribute('data-eb-per-head')) || 75;
  var PICKS = parseInt(root.getAttribute('data-eb-pick-count'), 10) || 3;
  var MIN_GUESTS = parseInt(root.getAttribute('data-eb-min-guests'), 10) || 0;

  var drinks = [].slice.call(root.querySelectorAll('[data-eb-class-drink]'));
  var guestsEl = root.querySelector('[data-eb-class-guests]');
  var countEl = root.querySelector('[data-eb-class-count]');
  var guestOut = root.querySelector('[data-eb-class-guest-out]');
  var picksOut = root.querySelector('[data-eb-class-picks-out]');
  var totalOut = root.querySelector('[data-eb-class-total]');
  var noteOut = root.querySelector('[data-eb-class-note]');
  var kindEl = root.querySelector('[data-eb-class-kind]');
  var kindNote = root.querySelector('[data-eb-class-kind-note]');
  var pickerEl = root.querySelector('[data-eb-class-picker]');
  var picksLabel = root.querySelector('[data-eb-class-picks-label]');
  var picksField = root.querySelector('[data-eb-class-picks-field]');
  var totalField = root.querySelector('[data-eb-class-total-field]');
  var noteDefault = noteOut ? noteOut.textContent : '';

  function money(n) {
    return '$' + Math.round(n).toLocaleString('en-US');
  }

  function chosen() {
    return drinks.filter(function (d) { return d.checked; });
  }

  function isCustom() {
    if (!kindEl) return true;
    var opt = kindEl.options[kindEl.selectedIndex];
    return !!(opt && opt.hasAttribute('data-custom'));
  }

  function themeDrinks() {
    if (!kindEl) return '';
    var opt = kindEl.options[kindEl.selectedIndex];
    return opt ? (opt.getAttribute('data-drinks') || '') : '';
  }

  function sync() {
    /* A themed class already has its three drinks, so the picker is not just
       unnecessary there — offering it would imply you can swap them. Collapse it
       and report the class's own trio instead. */
    var custom = isCustom();
    if (pickerEl) pickerEl.hidden = !custom;
    if (picksLabel) picksLabel.textContent = custom ? ('Your ' + PICKS + ' cocktails') : 'This class teaches';
    if (kindNote) {
      kindNote.textContent = custom
        ? 'Pick any ' + PICKS + ' from the list below.'
        : 'Fixed trio for this class. Want something else? Choose Custom.';
    }

    if (!custom) {
      var trio = themeDrinks();
      if (picksOut) picksOut.textContent = trio || '—';
      if (picksField) picksField.value = trio;
      if (countEl) countEl.textContent = '';
      drinks.forEach(function (d) { d.checked = false; d.disabled = false; });
      total();
      return;
    }

    var picked = chosen();
    var n = picked.length;

    /* At the cap, lock the rest rather than silently ignoring further clicks.
       A disabled box that visibly cannot be ticked explains the rule better
       than any sentence would. */
    drinks.forEach(function (d) {
      d.disabled = !d.checked && n >= PICKS;
    });

    if (countEl) {
      countEl.textContent = n + ' of ' + PICKS + ' chosen' +
        (n >= PICKS ? ' — untick one to swap' : '');
      if (n >= PICKS) {
        countEl.setAttribute('data-full', '');
      } else {
        countEl.removeAttribute('data-full');
      }
    }

    var names = picked.map(function (d) { return d.value; });
    if (picksOut) picksOut.textContent = names.length ? names.join(', ') : '—';
    if (picksField) picksField.value = names.join(', ');

    total();
    if (typeof refreshShare === 'function') refreshShare();
  }

  function total() {
    var guests = guestsEl ? parseInt(guestsEl.value, 10) : NaN;
    if (!isFinite(guests) || guests <= 0) {
      if (guestOut) guestOut.textContent = '—';
      if (totalOut) totalOut.textContent = '—';
      if (totalField) totalField.value = '';
      if (noteOut) noteOut.textContent = noteDefault;
      return;
    }

    if (guestOut) guestOut.textContent = guests.toLocaleString('en-US');

    /* Below the minimum there is no honest total to show. Saying so beats
       quoting a number we would not accept the booking at. */
    if (MIN_GUESTS && guests < MIN_GUESTS) {
      if (totalOut) totalOut.textContent = '—';
      if (totalField) totalField.value = '';
      if (noteOut) {
        noteOut.textContent = 'Classes start at ' + MIN_GUESTS +
          ' guests. Send it anyway and we will tell you what is possible.';
      }
      return;
    }

    var total = guests * PER_HEAD;
    if (totalOut) totalOut.textContent = money(total);
    if (totalField) totalField.value = money(total);
    if (noteOut) noteOut.textContent = noteDefault;
  }


  /* ==== Open classes ====================================================
     A class is open for payment when it exists as a variant on the class
     product: SKU CLASS-YYYY-MM-DD-HHMM, inventory = the seats we confirmed.

     This replaced a confirmation-code scheme that worked but made the host do
     the work — paste a code we emailed them, then copy and send twelve
     separate seat links so that twelve was all anyone could buy. Shopify
     already holds the thing that scheme was imitating: shared, server-side,
     race-safe stock. So the host gets one link, every guest gets the same one,
     and the last seat sells out because the platform says so.

     What the page shows about remaining seats is a snapshot from page load and
     can be a little stale. That is fine — it is a courtesy, not the gate. The
     gate is checkout, which cannot oversell.
     ====================================================================== */
  var SESSIONS = (function () {
    var el = root.querySelector('[data-eb-class-sessions]');
    if (!el) return [];
    try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
  })();

  /* A slot is YYYY-MM-DD-HHMM-NN: date, start time, and the seats we
     confirmed. The seat count lives in the SKU rather than being read off the
     stock level because stock falls as guests pay, while the seats stay
     committed to that class either way — and the daily cap is about what is
     committed, not about what is still unsold. */
  function slotParts(slot) {
    var raw = String(slot || '');
    var m = /^(\d{4}-\d{2}-\d{2})(?:-(\d{4}))?(?:-(\d+))?$/.exec(raw);
    if (!m) return { date: raw.slice(0, 10), hhmm: '', size: NaN };
    return { date: m[1], hhmm: m[2] || '', size: m[3] ? parseInt(m[3], 10) : NaN };
  }

  function slotDate(slot) { return slotParts(slot).date; }

  // Date-only, so a class this evening is still payable.
  function notPast(iso) {
    var p = String(iso).split('-');
    if (p.length !== 3) return false;
    var d = new Date(+p[0], +p[1] - 1, +p[2]);
    var n = new Date();
    return d >= new Date(n.getFullYear(), n.getMonth(), n.getDate());
  }

  /* Match on the date, and on the time too when we have one. Two classes on a
     day is legal — the daily cap is seats, not sessions — so the time is what
     tells them apart. Falling back to the day's only class when no time is set
     keeps the common case (one class, one date) free of extra questions. */
  function sessionFor(iso, hhmm) {
    if (!iso) return null;
    var exact = null, sameDay = null, dayCount = 0;
    for (var i = 0; i < SESSIONS.length; i++) {
      var v = SESSIONS[i];
      if (slotDate(v.slot) !== iso) continue;
      dayCount++;
      if (!sameDay) sameDay = v;
      if (hhmm && slotParts(v.slot).hhmm === hhmm.replace(':', '')) exact = v;
    }
    if (exact) return exact;
    /* A time was given and nothing matched it. Falling back to the day's other
       class here would quietly hand this host somebody else's class — and with
       it, somebody else's seats. */
    if (hhmm) return null;
    return dayCount === 1 ? sameDay : null;
  }

  function sessionById(id) {
    for (var i = 0; i < SESSIONS.length; i++) {
      if (String(SESSIONS[i].id) === String(id)) return SESSIONS[i];
    }
    return null;
  }

  function seatsFree(v) {
    if (!v) return 0;
    // An untracked variant has no meaningful count; treat it as open.
    if (!v.tracked) return Infinity;
    return Math.max(0, parseInt(v.left, 10) || 0);
  }

  /* ==== Invites =========================================================
     No accounts and no backend, so an invite is a URL that carries the plan.
     The host copies it; a guest opening it sees the invitation instead of the
     sales page, can add it to a calendar, and can reply.

     RSVPs come to us rather than to the host. We are the ones who need the final
     headcount, and a mailto: back to the host would break on half of phones.
     ====================================================================== */
  var MAX_INVITES = parseInt(root.getAttribute('data-eb-max-invites'), 10) || 20;

  /* ==== Daily capacity ==================================================
     Only DAY_CAP guests can be run across every class in a single day, so a
     date is a shared resource rather than an open field. Shopify inventory
     cannot express "20 per calendar day" without a variant per date, so the
     booked days are a maintained list and the check happens here, at the moment
     someone picks a date — which is the only moment it can change their mind
     cheaply.
     ====================================================================== */
  var DAY_CAP = parseInt(root.getAttribute('data-eb-day-capacity'), 10) || 20;
  var manualBooked = {};
  (root.getAttribute('data-eb-day-bookings') || '').split(/[\n,]+/).forEach(function (row) {
    var bits = row.split(':');
    if (bits.length < 2) return;
    var day = bits[0].trim();
    var n = parseInt(bits[1], 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(day) && isFinite(n)) {
      manualBooked[day] = (manualBooked[day] || 0) + n;
    }
  });

  var seatsEl = root.querySelector('[data-eb-seats]');
  var capacityField = root.querySelector('[data-eb-capacity-field]');

  /* What a day already owes. The open classes are the record: each commits
     its confirmed seats for the whole day, paid for or not. The day-bookings
     setting is now only for seats committed somewhere other than this site —
     a phone booking, an invoice — and it adds to the total rather than being
     the total, so there is no longer a second list to keep in step. */
  function committedOn(day, excludeId) {
    var total = manualBooked[day] || 0;
    for (var i = 0; i < SESSIONS.length; i++) {
      var p = slotParts(SESSIONS[i].slot);
      if (p.date !== day) continue;
      /* Skip the caller's own class. Their guests are committed once, inside
         that class; counting them a second time against the day would have the
         day tell a host their own booking has used up the room for it. */
      if (excludeId != null && String(SESSIONS[i].id) === String(excludeId)) continue;
      if (isFinite(p.size)) {
        total += p.size;
      } else {
        /* SKU without a seat count. What is still unsold is a floor rather
           than the truth, but it beats counting the class as nothing. */
        var unsold = seatsFree(SESSIONS[i]);
        total += isFinite(unsold) ? unsold : 0;
      }
    }
    return total;
  }

  function seatsLeft(day, excludeId) {
    if (!day) return null;
    return Math.max(0, DAY_CAP - committedOn(day, excludeId));
  }

  /* The room left in a day as it looks to THIS host: everything else that is
     committed, but not their own class. */
  function seatsLeftForHost(day) {
    var mine = openSession();
    return seatsLeft(day, mine ? mine.id : null);
  }

  function capacityState() {
    var day = dateEl ? dateEl.value : '';
    var left = seatsLeftForHost(day);
    var want = guestsEl ? parseInt(guestsEl.value, 10) : NaN;
    if (left === null) return { state: '', text: '', blocked: false };
    if (left === 0) {
      return {
        state: 'full', blocked: true,
        text: 'That day is fully booked. Pick another date.'
      };
    }
    /* Fewer seats left than the minimum class size means no bookable class
       fits, however small the party. Saying "6 of 20 seats left" there reads
       as availability and invites an enquiry that can only be declined. */
    if (MIN_GUESTS && left < MIN_GUESTS) {
      return {
        state: 'full', blocked: true,
        text: 'Only ' + left + ' of ' + DAY_CAP + ' seats are left that day, fewer than the ' +
              MIN_GUESTS + '-guest minimum. Pick another date.'
      };
    }
    if (isFinite(want) && want > left) {
      return {
        state: 'tight', blocked: true,
        text: 'Only ' + left + ' of ' + DAY_CAP + ' seats left that day, and you asked for ' +
              want + '. Trim the headcount or pick another date.'
      };
    }
    return {
      state: 'ok', blocked: false,
      text: left === DAY_CAP
        ? 'That day is open.'
        : left + ' of ' + DAY_CAP + ' seats left that day.'
    };
  }

  /* The daily cap is the one rule that cannot be a static Liquid attribute:
     it depends on the date the visitor just picked. Republish it onto the
     guest field and let eb-limits.js do the blocking, so capacity is enforced
     through the same path as every other minimum on the site rather than
     through a second, parallel implementation living here. */
  function publishCeiling() {
    if (!guestsEl) return;
    var day = dateEl ? dateEl.value : '';

    /* Once the class is open, the binding number is the size we confirmed, not
       what is left in the day — the day already has these seats. Typing a
       bigger number is not an overbooking, it is a change of plan, and it
       should read like one. */
    var mine = openSession();
    var mySize = mine ? slotParts(mine.slot).size : NaN;
    if (mine && isFinite(mySize)) {
      guestsEl.setAttribute('data-eb-max', mySize);
      guestsEl.setAttribute(
        'data-eb-max-msg',
        'We confirmed {max} seats for this class. Tell us if the number has changed and we will re-open it.'
      );
      if (window.EBLimits) window.EBLimits.check(guestsEl);
      return;
    }

    var left = seatsLeftForHost(day);
    var ceiling = left === null ? DAY_CAP : left;
    guestsEl.setAttribute('data-eb-max', ceiling);
    guestsEl.setAttribute(
      'data-eb-max-msg',
      left === 0
        ? 'That date is fully booked. Pick another day.'
        : left !== null && MIN_GUESTS && left < MIN_GUESTS
          ? 'Only {max} seats are left on that date, fewer than the ' + MIN_GUESTS +
            '-guest minimum. Pick another day.'
        : left !== null && left < DAY_CAP
          ? 'Only {max} of ' + DAY_CAP + ' seats are left on that date. Lower the count to {max} or fewer, or pick another day.'
          : 'We run at most {max} guests in a day across every class.'
    );
    if (window.EBLimits) window.EBLimits.check(guestsEl);
  }

  /* Travel. Previously the summary printed a fixed string and no code on the
     page could change it, so a class 40 miles out showed "Free within 20 mi".
     The distance and the fee both come from assets/eb-travel.js, which the
     estimate builder and the Crush Kit use too — one rule, three forms, and it
     matches what the refund policy promises.

     Not folded into the per-person total on purpose: travel is one charge for
     the visit, and the policy lets the host either carry it or split it across
     the guest count. Showing it as its own line is what makes that choice
     visible rather than burying it in a per-head figure. */
  var travelAmtEl = root.querySelector('[data-eb-class-travel]');
  var TRAVEL_FREE_TEXT = travelAmtEl ? travelAmtEl.textContent.trim() : '';

  function refreshTravel() {
    if (!travelAmtEl || !window.EBTravel) return;
    /* The panel gets an AMOUNT and nothing else. paint() puts the explanation
       beside the city and ZIP fields, where there is room for a sentence and
       where the person can actually act on it. A totals column is a stack of
       narrow figures; prose in there wrapped to ten lines and pushed the number
       it was annotating off the top of the row. */
    var d = window.EBTravel.paint(root);
    if (d.amount > 0) {
      travelAmtEl.textContent = money(d.amount);
    } else if (d.state === 'far') {
      travelAmtEl.textContent = 'Quoted';
    } else if (d.state === 'included') {
      travelAmtEl.textContent = 'Free';
    } else {
      travelAmtEl.textContent = TRAVEL_FREE_TEXT;
    }
  }

  function refreshCapacity() {
    var c = capacityState();
    publishCeiling();
    if (seatsEl) {
      seatsEl.textContent = c.text;
      if (c.state) { seatsEl.setAttribute('data-state', c.state); }
      else { seatsEl.removeAttribute('data-state'); }
    }
    if (capacityField) {
      capacityField.value = c.text ? (c.state.toUpperCase() + ' — ' + c.text) : '';
    }
    return c;
  }

  var sessionState = root.querySelector('[data-eb-session-state]');

  var share = root.querySelector('[data-eb-share]');
  var shareLink = root.querySelector('[data-eb-share-link]');
  var shareState = root.querySelector('[data-eb-share-state]');
  var dateEl = root.querySelector('[data-eb-class-date]');
  var timeEl = root.querySelector('[data-eb-class-time]');
  var placeEl = root.querySelector('#eb-class-place');
  var cityEl = root.querySelector('#eb-class-city');
  var hostEl = root.querySelector('#eb-class-name');

  function prettyDate(iso) {
    if (!iso) return '';
    var bits = iso.split('-');
    if (bits.length !== 3) return iso;
    var d = new Date(+bits[0], +bits[1] - 1, +bits[2]);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US',
      { weekday: 'long', month: 'long', day: 'numeric' });
  }

  function prettyTime(hhmm) {
    if (!hhmm) return '';
    var p = hhmm.split(':');
    if (p.length < 2) return hhmm;
    var h = +p[0], m = p[1];
    var ampm = h >= 12 ? 'pm' : 'am';
    h = h % 12 || 12;
    return h + (m === '00' ? '' : ':' + m) + ampm;
  }

  /* The class this host is arranging, if we have opened it yet. */
  function openSession() {
    return sessionFor(dateEl ? dateEl.value : '', timeEl ? timeEl.value : '');
  }

  function inviteUrl() {
    var q = [];
    function push(k, v) { if (v) q.push(k + '=' + encodeURIComponent(v)); }
    push('invite', '1');
    push('host', hostEl ? hostEl.value.trim() : '');
    push('cls', kindEl ? kindEl.value : '');
    push('drinks', picksField ? picksField.value : '');
    push('date', dateEl ? dateEl.value : '');
    push('time', timeEl ? timeEl.value : '');
    push('where', [placeEl && placeEl.value, cityEl && cityEl.value.trim()]
      .filter(Boolean).join(', '));

    /* Host contact rides along only on an explicit tick. The host is sending
       this to their own friends, but the link is still a URL that can be
       forwarded anywhere, so it is theirs to opt into rather than ours to
       assume. */
    /* Who pays travels in the link, so a guest can never be shown a pay button
       for a class the host already paid for, or left with no way to pay for one
       they were asked to cover. */
    var mode = root.querySelector('[data-eb-pay-mode]:checked');
    var wantsGuestPay = !!(mode && mode.value === 'guest');
    var sess = openSession();
    var payable = !!(sess && seatsFree(sess) > 0 && notPast(slotDate(sess.slot)));

    /* Three states, not two. "pending" exists because the wrong answer here is
       telling a guest the host has paid when the host has not — which is what
       falling back to 'host' would do the moment the class is not open yet.
       The same link becomes payable on its own once we open the class, so the
       host never has to send a second one. */
    push('pay', wantsGuestPay ? (payable ? 'guest' : 'pending') : 'host');
    if (wantsGuestPay && payable) push('v', String(sess.id));

    var optin = root.querySelector('[data-eb-share-optin]');
    if (optin && optin.checked) {
      var em = root.querySelector('#eb-class-email');
      var ph = root.querySelector('#eb-class-phone');
      push('hemail', em ? em.value.trim() : '');
      push('hphone', ph ? ph.value.trim() : '');
    }
    return location.origin + location.pathname + '?' + q.join('&');
  }

  function icsHref(title, when, where, body) {
    /* A local floating time: no VTIMEZONE, so the hour a host typed is the hour
       the guest sees rather than being shifted by their device zone. */
    function stamp(d) {
      return d.getFullYear() +
        ('0' + (d.getMonth() + 1)).slice(-2) +
        ('0' + d.getDate()).slice(-2) + 'T' +
        ('0' + d.getHours()).slice(-2) +
        ('0' + d.getMinutes()).slice(-2) + '00';
    }
    if (!when || isNaN(when.getTime())) return '';
    var end = new Date(when.getTime() + 60 * 60 * 1000);
    var esc = function (v) { return String(v || '').replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n'); };
    var lines = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Executive Beverage//Cocktail Class//EN',
      'BEGIN:VEVENT', 'UID:' + stamp(when) + '-ebclass@execbeverage.com',
      'DTSTAMP:' + stamp(new Date(2020, 0, 1)),
      'DTSTART:' + stamp(when), 'DTEND:' + stamp(end),
      'SUMMARY:' + esc(title), 'LOCATION:' + esc(where), 'DESCRIPTION:' + esc(body),
      'END:VEVENT', 'END:VCALENDAR'
    ];
    return 'data:text/calendar;charset=utf-8,' + encodeURIComponent(lines.join('\r\n'));
  }

  function refreshShare() {
    if (!share) return;
    /* Only worth showing once there is something worth sharing. A link with no
       date and no class in it tells a guest nothing. */
    var ready = !!(dateEl && dateEl.value) || !!(hostEl && hostEl.value.trim());
    share.hidden = !ready;
    if (!ready) return;

    var url = inviteUrl();
    if (shareLink) shareLink.value = url;

    var host = hostEl && hostEl.value.trim();
    var subject = (host ? host + ' invited you' : 'You are invited') + ' to a cocktail class';
    var blurb = subject + '\n\n' + url;

    var sms = root.querySelector('[data-eb-share-sms]');
    var mail = root.querySelector('[data-eb-share-email]');
    if (sms) sms.href = 'sms:?&body=' + encodeURIComponent(blurb);
    if (mail) mail.href = 'mailto:?subject=' + encodeURIComponent(subject) +
      '&body=' + encodeURIComponent(blurb);

    /* Host checkout: quantity follows the headcount, and the line carries the
       class, date and host so the order is matchable to the booking request. */
    var payForm = root.querySelector('[data-eb-pay-form]');
    var payMode = root.querySelector('[data-eb-pay-mode]:checked');
    var hostPays = !payMode || payMode.value === 'host';
    var headcount = guestsEl ? parseInt(guestsEl.value, 10) : NaN;

    var cap = refreshCapacity();
    var hostSession = openSession();
    if (payForm) {
      /* Never sell a seat on a day we cannot run — a refund is a worse
         experience than a message — and never before the class exists, because
         the thing the host is buying IS the class: their seats come out of that
         variant's stock. */
      var sessionSeats = seatsFree(hostSession);
      var payable = hostPays && isFinite(headcount) && headcount > 0 &&
        !cap.blocked && !!hostSession && sessionSeats > 0;
      payForm.hidden = !payable;
      if (payable) {
        var buying = sessionSeats === Infinity ? headcount : Math.min(headcount, sessionSeats);
        var vIn = payForm.querySelector('[data-eb-pay-variant]');
        if (vIn) vIn.value = String(hostSession.id);
        var q1 = payForm.querySelector('[data-eb-pay-qty]');
        if (q1) q1.value = String(buying);
        var pc = payForm.querySelector('[data-eb-pay-prop-class]');
        var pd = payForm.querySelector('[data-eb-pay-prop-date]');
        var ph2 = payForm.querySelector('[data-eb-pay-prop-host]');
        if (pc) pc.value = kindEl ? kindEl.value : '';
        if (pd) pd.value = [dateEl && dateEl.value, timeEl && timeEl.value].filter(Boolean).join(' ');
        if (ph2) ph2.value = hostEl ? hostEl.value.trim() : '';
        var btn = payForm.querySelector('[data-eb-pay-submit]');
        var note = payForm.querySelector('[data-eb-pay-note]');
        var unit = isFinite(hostSession.price) ? hostSession.price / 100 : PER_HEAD;
        if (btn) {
          btn.textContent = 'Pay for ' + buying + ' guest' + (buying === 1 ? '' : 's') +
            ' — ' + money(buying * unit);
        }
        if (note) {
          note.textContent = buying < headcount
            ? 'Only ' + buying + ' seat' + (buying === 1 ? ' is' : 's are') +
              ' still unpaid on this class, so that is what this covers.'
            : 'Secure Shopify checkout. Nothing is charged until you confirm.';
        }
      }
    }

    /* One line telling the host where payment stands. No field to fill in:
       the class is recognised from the date they already entered. */
    if (sessionState) {
      var guestPaySelected = !!(payMode && payMode.value === 'guest');
      var free = seatsFree(hostSession);
      var freeLabel = free === Infinity
        ? 'Seats'
        : free + ' seat' + (free === 1 ? '' : 's');
      sessionState.hidden = false;
      sessionState.removeAttribute('data-state');
      if (!hostSession) {
        sessionState.textContent = 'Send your request first. We open the class for payment once the date is ' +
          'confirmed, and this same link starts taking payments the moment we do — you will not have to send it again.';
      } else if (free <= 0) {
        sessionState.textContent = 'Every seat on ' + prettyDate(dateEl.value) +
          ' has been paid for. Talk to us before you invite anyone else.';
      } else {
        sessionState.setAttribute('data-state', 'open');
        sessionState.textContent = 'Open for payment. ' + freeLabel + ' still unpaid — ' +
          (guestPaySelected
            ? 'share the one link above with everyone. It stops taking payments by itself when the last seat goes.'
            : 'pay for them below, or switch to guests paying and send that same link.');
      }
    }

    var guests = headcount;
    if (shareState) {
      shareState.textContent = (isFinite(guests) && guests > MAX_INVITES)
        ? 'Heads up: the invite link covers ' + MAX_INVITES +
          ' guests. Tell us about the rest and we will sort it.'
        : '';
    }
  }

  var optinEl = root.querySelector('[data-eb-share-optin]');
  if (dateEl) {
    dateEl.addEventListener('change', refreshCapacity);
    dateEl.addEventListener('input', refreshCapacity);
  }
  if (guestsEl) guestsEl.addEventListener('input', refreshCapacity);
  [].slice.call(root.querySelectorAll('[data-eb-pay-mode]')).forEach(function (r) {
    r.addEventListener('change', refreshShare);
  });
  [shareLink && 'skip', dateEl, timeEl, placeEl, cityEl, hostEl, guestsEl, kindEl, optinEl,
   root.querySelector('#eb-class-email'), root.querySelector('#eb-class-phone')]
    .forEach(function (el) {
      if (!el || el === 'skip') return;
      el.addEventListener('input', refreshShare);
      el.addEventListener('change', refreshShare);
    });

  var copyBtn = root.querySelector('[data-eb-share-copy]');
  if (copyBtn) {
    copyBtn.addEventListener('click', function () {
      if (!shareLink) return;
      shareLink.select();
      var done = false;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(shareLink.value);
        done = true;
      } else {
        try { done = document.execCommand('copy'); } catch (e) { done = false; }
      }
      if (shareState) {
        shareState.textContent = done ? 'Link copied.' : 'Press copy on your keyboard to grab it.';
      }
    });
  }

  /* ---- guest view -------------------------------------------------------
     A guest is not a prospect mid-consideration; they have been invited to a
     thing that is already paid for. So this does not hide two blocks of the
     sales page — it hides EVERYTHING except the invitation, including the
     booking form and the closing CTA, and shows a screen built for the three
     questions a guest actually has: when, where, and am I bringing anything.

     Hiding by "everything that is not the invitation" rather than by name, so a
     section added to this page later cannot leak into the guest screen. */
  (function invited() {
    var q = new URLSearchParams(location.search);
    if (!q.get('invite')) return;

    var wrap = root.querySelector('[data-eb-invite]');
    if (!wrap) return;

    var host = q.get('host') || '';
    var cls = q.get('cls') || '';
    var trio = q.get('drinks') || '';
    var when = prettyDate(q.get('date'));
    var at = prettyTime(q.get('time'));
    var where = q.get('where') || '';

    function set(sel, value, hideIfEmpty) {
      var el = wrap.querySelector(sel);
      if (!el) return;
      if (!value) { if (hideIfEmpty) el.hidden = true; else el.textContent = ''; return; }
      el.hidden = false;
      el.textContent = value;
    }

    set('[data-eb-invite-kicker]', 'You are invited');
    set('[data-eb-invite-title]', host
      ? host + ' is hosting a private cocktail class.'
      : 'You are invited to a private cocktail class.');
    set('[data-eb-invite-when-big]', [when, at].filter(Boolean).join(' · '));
    set('[data-eb-invite-where-big]', where);

    if (cls || trio) {
      var block = wrap.querySelector('[data-eb-invite-classblock]');
      if (block) block.hidden = false;
      set('[data-eb-invite-classname]', cls);
      set('[data-eb-invite-drinklist]', trio ? 'You will make: ' + trio : '');
    }

    /* Days-away reads as a nudge, not a countdown clock. Uses date-only maths so
       a class this evening still says "today" rather than "in 0 days". */
    var iso = q.get('date');
    if (iso) {
      var parts = iso.split('-');
      var target = new Date(+parts[0], +parts[1] - 1, +parts[2]);
      var now = new Date();
      var today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      var days = Math.round((target - today) / 86400000);
      var label = '';
      if (days === 0) label = 'Today';
      else if (days === 1) label = 'Tomorrow';
      else if (days > 1) label = 'In ' + days + ' days';
      if (label) set('[data-eb-invite-countdown]', label, true);
    }

    var hostField = wrap.querySelector('[data-eb-rsvp-host]');
    var detailField = wrap.querySelector('[data-eb-rsvp-details]');
    if (hostField) hostField.value = host;
    if (detailField) {
      detailField.value = [cls, [when, at].filter(Boolean).join(' '), where, trio]
        .filter(Boolean).join(' | ');
    }

    var ics = wrap.querySelector('[data-eb-invite-ics]');
    if (ics) {
      var d = q.get('date'), tm = q.get('time') || '18:00';
      var start = d ? new Date(d + 'T' + tm) : null;
      var href = start ? icsHref(
        (cls ? cls : 'Cocktail class') + (host ? ' with ' + host : ''),
        start, where, trio) : '';
      if (href) { ics.href = href; } else { ics.hidden = true; }
    }

    var hEmail = q.get('hemail') || '';
    var hPhone = q.get('hphone') || '';
    var msgEmail = wrap.querySelector('[data-eb-invite-msg-email]');
    var msgSms = wrap.querySelector('[data-eb-invite-msg-sms]');
    var guestName = wrap.querySelector('#eb-rsvp-name');
    var comingEls = [].slice.call(wrap.querySelectorAll('[data-eb-rsvp-coming]'));

    function coming() {
      for (var i = 0; i < comingEls.length; i++) {
        if (comingEls[i].checked) return /yes/i.test(comingEls[i].value);
      }
      return true;
    }

    function hostMessage() {
      var who = guestName && guestName.value.trim();
      var w = [when, at].filter(Boolean).join(' ');
      return (who ? who + ' here — ' : '') +
        (coming() ? 'I am in for the cocktail class' : 'Sorry, I cannot make the cocktail class') +
        (w ? ' on ' + w : '') + '.';
    }

    function refreshHostMsg() {
      var subject = (cls ? cls : 'Cocktail class') + (host ? ' with ' + host : '');
      if (msgEmail) {
        msgEmail.hidden = !hEmail;
        if (hEmail) {
          msgEmail.textContent = host ? 'Email ' + host : 'Email the host';
          msgEmail.href = 'mailto:' + encodeURIComponent(hEmail) +
            '?subject=' + encodeURIComponent(subject) +
            '&body=' + encodeURIComponent(hostMessage());
        }
      }
      if (msgSms) {
        msgSms.hidden = !hPhone;
        if (hPhone) {
          msgSms.textContent = host ? 'Text ' + host : 'Text the host';
          msgSms.href = 'sms:' + hPhone.replace(/[^+0-9]/g, '') +
            '?&body=' + encodeURIComponent(hostMessage());
        }
      }
    }
    if (guestName) guestName.addEventListener('input', refreshHostMsg);
    comingEls.forEach(function (r) { r.addEventListener('change', refreshHostMsg); });
    refreshHostMsg();

    /* Pay path. Default to the host having paid: if the parameter is missing for
       any reason, the safe failure is telling a guest they owe nothing rather
       than asking them for money that may already be paid.

       Asking for money needs more than a parameter, though. Every one of these
       has to hold: we confirmed the class, the confirmation is for the date on
       this invitation, the class has not happened yet, and this link is one of
       the seats we confirmed. Anything else shows an explanation instead of a
       button — silence would read as "nothing to pay". */
    var payParam = q.get('pay');
    var hostPaid = payParam !== 'guest' && payParam !== 'pending';
    var sess = sessionById(q.get('v'));
    var free = seatsFree(sess);

    var guestsPay = false;
    var payReason = '';
    /* Distinguishes "not yet" from "never". A guest waiting on a class we have
       not opened will get a seat link; a guest looking at a sold-out or past
       class will not, and telling them one is on its way is a small lie the
       host has to clear up. */
    var seatLinkComing = payParam === 'pending';
    if (payParam === 'guest') {
      if (!sess) {
        seatLinkComing = true;
        payReason = 'This class is not open for payment yet. Your host will hear from us when it is — nothing for you to do.';
      } else if (!notPast(slotDate(sess.slot))) {
        payReason = 'This class has already happened.';
      } else if (free <= 0) {
        payReason = 'Every seat for this class has been paid for. Check with your host before paying anything.';
      } else {
        guestsPay = true;
      }
    } else if (payParam === 'pending') {
      payReason = 'Your host is still arranging payment with us. Nothing for you to do right now.';
    }

    var payBlock = wrap.querySelector('[data-eb-guest-pay]');
    var payPending = wrap.querySelector('[data-eb-guest-pay-pending]');
    if (payBlock) payBlock.hidden = !guestsPay;
    if (payPending) {
      payPending.hidden = !payReason;
      if (payReason) payPending.textContent = payReason;
    }
    if (guestsPay && payBlock) {
      var gp = {
        cls: payBlock.querySelector('[data-eb-guest-prop-class]'),
        date: payBlock.querySelector('[data-eb-guest-prop-date]'),
        host: payBlock.querySelector('[data-eb-guest-prop-host]'),
        name: payBlock.querySelector('[data-eb-guest-prop-name]')
      };

      /* Point the form at this class's variant. Its stock is the seat count,
         so checkout is what refuses the seat after the last one — not us. */
      var variantIn = payBlock.querySelector('[data-eb-guest-variant]');
      if (variantIn && sess) variantIn.value = String(sess.id);

      var leftEl = wrap.querySelector('[data-eb-guest-seatsleft]');
      if (leftEl && sess && free !== Infinity) {
        leftEl.hidden = false;
        leftEl.textContent = free === 1 ? 'One seat left.' : free + ' seats left.';
        if (free <= 3) leftEl.setAttribute('data-state', 'low');
        else leftEl.removeAttribute('data-state');
      }
      if (gp.cls) gp.cls.value = cls;
      if (gp.date) gp.date.value = [q.get('date'), q.get('time')].filter(Boolean).join(' ');
      if (gp.host) gp.host.value = host;
      if (gp.name && guestName) {
        guestName.addEventListener('input', function () { gp.name.value = guestName.value.trim(); });
      }
    }

    /* The money line has to agree with the pay mode or the invitation lies. */
    var factLi = wrap.querySelector('[data-eb-fact-pay]');
    if (factLi) {
      var span = factLi.querySelector('[data-eb-fact-text]');
      /* Three states, because there are three truths. Keyed off what the host
         chose rather than off whether the button rendered: a guest whose seat
         link has not arrived still owes for their seat, so "already paid for"
         would be a lie the host has to undo. But the normal guests-pay line
         points at a Pay button, so when there is no button to point at, it
         needs its own wording. */
      var copy = hostPaid
        ? root.getAttribute('data-eb-fact-hostpaid')
        : (guestsPay
            ? root.getAttribute('data-eb-fact-guestpays')
            : (seatLinkComing ? root.getAttribute('data-eb-fact-guestpending') : ''));
      /* Nothing true left to say about money: the line above the fold goes
         rather than saying something wrong, and the block below explains. */
      if (!copy) factLi.hidden = true;
      if (span && copy) span.textContent = copy;
    }

    var own = wrap.querySelector('[data-eb-invite-own]');
    if (own) own.href = location.pathname;

    wrap.hidden = false;

    /* Everything else on the page goes. */
    var siblings = wrap.parentElement ? wrap.parentElement.children : [];
    [].slice.call(siblings).forEach(function (el) { if (el !== wrap) el.hidden = true; });
    var closing = document.querySelector('.eb-cta-section');
    if (closing) closing.hidden = true;
    root.setAttribute('data-eb-invite-mode', '');
  })();

  [].slice.call(root.querySelectorAll('[data-eb-travel-city], [data-eb-travel-zip]'))
    .forEach(function (el) {
      el.addEventListener('input', refreshTravel);
      el.addEventListener('change', refreshTravel);
    });
  refreshTravel();

  drinks.forEach(function (d) { d.addEventListener('change', sync); });
  if (kindEl) kindEl.addEventListener('change', sync);
  if (guestsEl) {
    guestsEl.addEventListener('input', sync);
    guestsEl.addEventListener('change', sync);
  }

  /* ---- Choosing a themed class from its card --------------------------- */
  [].slice.call(root.querySelectorAll('[data-eb-class-choose]')).forEach(function (btn) {
    btn.addEventListener('click', function () {
      var want = btn.getAttribute('data-eb-class-choose');
      if (kindEl) {
        for (var i = 0; i < kindEl.options.length; i++) {
          if (kindEl.options[i].value === want) { kindEl.selectedIndex = i; break; }
        }
      }
      sync();
      var form = document.getElementById('eb-class-book');
      if (form && form.scrollIntoView) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  var customBtn = root.querySelector('[data-eb-class-custom]');
  if (customBtn && kindEl) {
    customBtn.addEventListener('click', function () {
      kindEl.selectedIndex = kindEl.options.length - 1;
      sync();
      var book = document.getElementById('eb-class-book');
      if (book && book.scrollIntoView) book.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  sync();
  refreshShare();
  refreshCapacity();
})();
